import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, saveBlobAs } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, FileText, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function SummaryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [recommendation, setRecommendation] = useState("go");
  const [notes, setNotes] = useState("");
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [d, cl] = await Promise.all([api.getSession(id), api.getChecklist()]);
        setData(d);
        setChecklist(cl);
        if (d.session.overall_recommendation) {
          setNotes(d.session.overall_recommendation);
        }
      } catch (e) {
        toast.error("Failed to load summary");
        navigate("/dashboard");
      }
    })();
  }, [id, navigate]);

  const stats = useMemo(() => {
    if (!data || !checklist) return null;
    const results = data.results;
    const total = checklist.sections.reduce((n, s) => n + s.cases.length, 0);
    const pass = results.filter((r) => r.status === "pass").length;
    const fail = results.filter((r) => r.status === "fail").length;
    const skipped = results.filter((r) => r.status === "skipped").length;
    const blocked = results.filter((r) => r.status === "blocked").length;
    const executed = pass + fail + skipped + blocked;
    const remaining = total - executed;
    return { total, pass, fail, skipped, blocked, executed, remaining };
  }, [data, checklist]);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const recText =
        (recommendation === "go"
          ? "GO — Overall recommendation is to proceed."
          : recommendation === "conditional-go"
          ? "CONDITIONAL GO — Proceed with noted defects tracked."
          : "NO-GO — Blocker defects prevent release.") +
        (notes ? `\n\nNotes: ${notes}` : "");
      await api.completeSession(id, {
        overall_recommendation: recText,
        status: "completed",
      });
      toast.success("Session signed off");
      navigate(`/session/${id}`);
    } catch (e) {
      toast.error("Failed to complete session");
    } finally {
      setCompleting(false);
    }
  };

  if (!data || !checklist || !stats) {
    return <div className="p-10 text-center text-slate-500">Loading summary…</div>;
  }

  const s = data.session;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2">Session Summary</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
            {s.tester_name}'s UAT run
          </h1>
          <p className="mt-2 text-sm text-slate-600 font-mono">
            Started {new Date(s.started_at).toLocaleString()} · Session {s.id.slice(0, 8)}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to={`/session/${id}/run`}>
            <Button
              variant="outline"
              data-testid="back-to-runner-btn"
              className="rounded-none border-slate-300 h-10"
            >
              <ArrowLeft size={14} className="mr-2" />
              Back to runner
            </Button>
          </Link>
          <Button
            variant="outline"
            data-testid="summary-export-btn"
            onClick={async () => {
              try {
                const res = await api.downloadSessionCsv(id);
                saveBlobAs(res.data, `uat-session-${id.slice(0, 8)}.csv`);
              } catch {
                toast.error("Failed to export CSV");
              }
            }}
            className="rounded-none border-slate-300 h-10"
          >
            <Download size={14} className="mr-2" />
            Export CSV
          </Button>
          <Button
            data-testid="summary-export-pdf-btn"
            onClick={async () => {
              try {
                const res = await api.downloadSessionPdf(id);
                saveBlobAs(res.data, `uat-session-${id.slice(0, 8)}.pdf`);
              } catch {
                toast.error("Failed to export PDF");
              }
            }}
            className="rounded-none bg-keplerio-navy hover:bg-keplerio-navy-dark text-white h-10"
          >
            <FileText size={14} className="mr-2" />
            PDF report
          </Button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatBlock label="Total" value={stats.total} testId="stat-total" />
        <StatBlock label="Executed" value={stats.executed} testId="stat-executed" />
        <StatBlock label="Passed" value={stats.pass} tone="pass" testId="stat-pass" />
        <StatBlock label="Failed" value={stats.fail} tone="fail" testId="stat-fail" />
        <StatBlock label="Skipped" value={stats.skipped} testId="stat-skipped" />
        <StatBlock label="Blocked" value={stats.blocked} testId="stat-blocked" />
      </div>

      {/* Recommendation */}
      <div className="bg-white border border-slate-200 p-6 md:p-8">
        <h2 className="font-heading text-lg font-semibold tracking-tight mb-4">
          Overall recommendation
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="overline text-slate-500">Verdict</Label>
            <Select value={recommendation} onValueChange={setRecommendation}>
              <SelectTrigger
                data-testid="recommendation-select"
                className="mt-2 rounded-none border-slate-300 focus:ring-1 focus:ring-blue-500"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="go" data-testid="rec-go">GO</SelectItem>
                <SelectItem value="conditional-go" data-testid="rec-conditional-go">
                  CONDITIONAL GO
                </SelectItem>
                <SelectItem value="no-go" data-testid="rec-no-go">NO-GO</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-slate-500">Sign-off notes</Label>
            <Textarea
              data-testid="signoff-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Blocker defects, follow-ups, sign-off comments…"
              className="mt-2 rounded-none border-slate-300 min-h-[80px] focus-visible:ring-1 focus-visible:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleComplete}
            disabled={completing || s.status === "completed"}
            data-testid="complete-session-btn"
            className="bg-keplerio-navy hover:bg-keplerio-navy-dark text-white rounded-none h-11 px-6"
          >
            <CheckCircle2 size={16} className="mr-2" />
            {s.status === "completed"
              ? "Already signed off"
              : completing
              ? "Saving…"
              : "Sign off session"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatBlock({ label, value, tone, testId }) {
  const toneCls = {
    pass: "text-emerald-700",
    fail: "text-red-700",
  }[tone] || "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 p-4" data-testid={testId}>
      <div className={`font-heading text-3xl font-bold tracking-tight ${toneCls}`}>
        {value}
      </div>
      <div className="overline mt-1">{label}</div>
    </div>
  );
}
